/**
 * IndexedDB schema v1 — Prompt.md §27, §7.
 *
 * Một trong ba chỗ duy nhất của app chạm browser API trực tiếp (cùng camera và vision provider).
 * Tầng trên chỉ thấy `SessionRepository` của `session-core`.
 */

import type { SessionSnapshot } from "@studystraight/session-core";
import type { StudySession } from "@studystraight/shared-types";
import { openDB, type DBSchema, type IDBPDatabase } from "idb";

export const DB_NAME = "studystraight";
export const DB_VERSION = 1;

/** Chỉ có đúng một snapshot dở dang tại một thời điểm → key cố định thay vì tự sinh. */
export const PENDING_SNAPSHOT_KEY = "pending";

export interface StudyStraightDB extends DBSchema {
  sessions: {
    key: string;
    value: StudySession;
    indexes: {
      /** History (§27) luôn sắp theo thời gian giảm dần — index này tránh full scan. */
      "by-startTime": number;
      /** Chưa dùng trong MVP; có sẵn cho §28 (Parent → Child → Session). */
      "by-childId": string;
    };
  };
  snapshots: {
    key: string;
    value: SessionSnapshot;
  };
  /** Config override của Settings > Developer + baseline gần nhất, dạng key-value. */
  settings: {
    key: string;
    value: unknown;
  };
}

export type StudyStraightDatabase = IDBPDatabase<StudyStraightDB>;

/**
 * Mở database.
 *
 * Có thể **throw**: Safari private mode và một số WebView chặn IndexedDB hoàn toàn. Caller
 * (`IndexedDBSessionRepository.create`) phải bắt và chuyển sang in-memory kèm cảnh báo cho người
 * dùng — fail âm thầm ở đây nghĩa là phụ huynh mất lịch sử mà không ai nói gì.
 */
export async function openStudyStraightDB(): Promise<StudyStraightDatabase> {
  if (typeof indexedDB === "undefined") {
    throw new Error("IndexedDB không có trên môi trường này");
  }

  return openDB<StudyStraightDB>(DB_NAME, DB_VERSION, {
    upgrade(db) {
      if (!db.objectStoreNames.contains("sessions")) {
        const sessions = db.createObjectStore("sessions", { keyPath: "id" });
        sessions.createIndex("by-startTime", "startTime");
        sessions.createIndex("by-childId", "childId");
      }
      if (!db.objectStoreNames.contains("snapshots")) {
        db.createObjectStore("snapshots");
      }
      if (!db.objectStoreNames.contains("settings")) {
        db.createObjectStore("settings");
      }
    },
    blocked() {
      // Tab khác đang giữ version cũ. Không throw: bản hiện tại vẫn chạy được, chỉ là chưa upgrade.
    },
  });
}
